import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PhoneCall } from "lucide-react";

const PERIODS = [["today", "Today"], ["week", "This week"], ["month", "This month"]];
const dur = (s) => (s == null ? "—" : s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

const Stat = ({ label, value, testId }) => (
  <div className="border border-[#E6E4DD] rounded-sm px-4 py-3 bg-white min-w-0">
    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-forest/50 truncate">{label}</div>
    <div data-testid={testId} className="font-display font-bold text-2xl text-forest mt-1 tabular-nums">{value}</div>
  </div>
);

// CallerDesk call stats from callerdesk_calls with today / week / month filters.
export const CallerDeskReport = ({ testPrefix = "cd-report" }) => {
  const [period, setPeriod] = useState("today");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/callerdesk/report", { params: { period } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [period]);
  const top = (k) => d?.by_executive?.filter((e) => e[k] > 0).sort((a, b) => b[k] - a[k])[0];
  return (
    <div data-testid={testPrefix} className="border border-[#E6E4DD] bg-bone-alt/30 rounded-sm p-4 sm:p-5 space-y-4 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><PhoneCall className="h-4 w-4 text-forest" /><h3 className="font-display font-bold text-lg text-forest">CallerDesk calls</h3></div>
        <div className="flex rounded-sm border border-[#E6E4DD] bg-white overflow-hidden">
          {PERIODS.map(([k, l]) => (
            <button key={k} data-testid={`${testPrefix}-period-${k}`} onClick={() => setPeriod(k)}
              className={`px-3 h-8 text-xs font-medium transition-colors duration-150 ${period === k ? "bg-forest text-white" : "text-forest/70 hover:bg-bone"}`}>{l}</button>
          ))}
        </div>
      </div>
      {!d ? <div className="text-sm text-forest/50">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            <Stat label="Total calls" value={d.total_calls} testId={`${testPrefix}-total`} />
            <Stat label="Unique callers" value={d.unique_callers} testId={`${testPrefix}-unique`} />
            <Stat label="Answered" value={d.answered} testId={`${testPrefix}-answered`} />
            <Stat label="Missed" value={d.missed} testId={`${testPrefix}-missed`} />
            <Stat label="Avg talk time" value={dur(d.avg_talk_sec)} testId={`${testPrefix}-avg-talk`} />
            <Stat label="Avg wait / queue" value={dur(d.avg_wait_sec)} testId={`${testPrefix}-avg-wait`} />
          </div>
          {d.by_executive.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-forest/70">
                {top("answered") && <span data-testid={`${testPrefix}-top-answered`}>Most answered: <b className="text-forest">{top("answered").name}</b> ({top("answered").answered})</span>}
                {top("missed") && <span data-testid={`${testPrefix}-top-missed`}>Most missed: <b className="text-clay">{top("missed").name}</b> ({top("missed").missed})</span>}
              </div>
              <div className="overflow-x-auto border border-[#E6E4DD] rounded-sm bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-bone-alt/40 text-[10px] uppercase tracking-[0.15em] text-forest/50">
                    <tr>{["Executive", "Calls", "Answered", "Missed", "Talk time"].map((h) => <th key={h} className="text-left px-3 py-2 font-bold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {d.by_executive.map((e) => (
                      <tr key={e.user_id} data-testid={`${testPrefix}-exec-${e.user_id}`} className="border-t border-[#E6E4DD]">
                        <td className="px-3 py-2 text-forest">{e.name}</td><td className="px-3 py-2 tabular-nums">{e.total}</td>
                        <td className="px-3 py-2 tabular-nums">{e.answered}</td><td className={`px-3 py-2 tabular-nums ${e.missed > 0 ? "text-clay" : ""}`}>{e.missed}</td>
                        <td className="px-3 py-2 tabular-nums">{dur(e.talk_sec)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
